import React, { useCallback, useMemo, useRef } from "react";
import { debounce } from "lodash";
import { ViewportList } from "react-viewport-list";
import Spinner from "@src/lib/ui/Spinner";
import { isNullOrUndefined } from "@src/lib/utils/typeguards";

const LIMIT = 40;

// These types are copied from PaginatedTable for compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import type {
  QueryHookOptions,
  QueryResult,
  LazyQueryResultTuple,
} from "@apollo/client";
import type { InputMaybe, PaginationInput } from "@src/types.generated";
import { cn } from "@src/lib/utils/cn";

// Variable shape expected by paginated query hooks
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PaginatedVariables = {
  input: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  pagination?: InputMaybe<PaginationInput> | undefined;
};

// QueryHook and LazyQueryHook types
// Apollo Client 3.8+ generates hooks that require `variables` to be explicitly
// provided (or `skip: true`), so we mirror that intersection here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QueryHook = (
  baseOptions: QueryHookOptions<
    any, // eslint-disable-line @typescript-eslint/no-explicit-any
    PaginatedVariables
  > &
    ({ variables: PaginatedVariables; skip?: boolean } | { skip: boolean }),
) => QueryResult<any, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LazyQueryHook = (
  baseOptions: QueryHookOptions<
    any, // eslint-disable-line @typescript-eslint/no-explicit-any
    PaginatedVariables
  >,
) => LazyQueryResultTuple<any, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export type PaginatedListProps<T, Hook extends QueryHook> = {
  queryHook: Hook;
  getItems: (data: ReturnType<Hook>["data"]) => T[];
  itemId: (item: T) => string | number;
  renderItem: (item: T) => React.ReactNode;
  input?: NonNullable<Parameters<QueryHook>[0]["variables"]>["input"];
  noDataEmptyState?: React.ReactNode;
  dataTestId?: string;
  getTotalItemCount?: (data: ReturnType<Hook>["data"]) => number;
  className?: string;
  viewportRef?: React.RefObject<HTMLElement | null>;
  paginationLimit?: number;
};

export default function PaginatedList<T, Hook extends QueryHook>({
  queryHook,
  getItems,
  itemId,
  renderItem,
  input,
  noDataEmptyState,
  dataTestId,
  getTotalItemCount,
  className,
  viewportRef,
  paginationLimit = LIMIT,
}: PaginatedListProps<T, Hook>): React.ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const { data, loading, fetchMore, error } = queryHook({
    variables: {
      input: input || {},
      pagination: {
        offset: 0,
        limit: paginationLimit,
      },
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const items = useMemo(() => getItems(data), [data, getItems]);
  const itemCount = items.length;
  // Robust hasMore logic: use total count if available, else fallback
  const totalCount = getTotalItemCount ? getTotalItemCount(data) : undefined;
  const hasMore =
    typeof totalCount === "number"
      ? items.length < totalCount
      : !isNullOrUndefined(data) &&
        itemCount % paginationLimit === 0 &&
        itemCount !== 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchMore = useCallback(
    debounce(() => {
      fetchMore({
        variables: {
          input: input || {},
          pagination: {
            offset: items.length,
            limit: paginationLimit,
          },
        },
      });
    }, 500),
    [fetchMore, input, items.length, paginationLimit],
  );

  if (loading && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center p-8 text-red-5">
        Error loading data
      </div>
    );
  }

  if (itemCount === 0) {
    return (
      <div className="flex justify-center p-8" data-testid={dataTestId}>
        {noDataEmptyState || "Nothing to display"}
      </div>
    );
  }

  return (
    <div ref={ref} data-testid={dataTestId} className={cn("w-full", className)}>
      <ViewportList
        items={items}
        itemMinSize={42}
        viewportRef={viewportRef}
        onViewportIndexesChange={([, end]) => {
          if (hasMore && end >= items.length - 1) {
            debouncedFetchMore();
          }
        }}
      >
        {(item) => <div key={itemId(item)}>{renderItem(item)}</div>}
      </ViewportList>
      {hasMore && (
        <div className="flex justify-center p-4">
          <Spinner />
        </div>
      )}
    </div>
  );
}
